import getSundaysForCurrentAndNextYear from './getSundaysForCurrentAndNextYear';
import { LOCALE_DATE_FORMAT } from './returnDateCalculator/constants';
import { parseUkDateString, formatUkDateISO } from './date/ukFrame';
import { daysBetween } from './returnDateCalculator/dateUtils';

const closedDatesCalculator = (
    closedDates: string[],
    targetDate: string
): {
    isInArray: boolean;
    consecutiveDays: number;
    nextOpenDay: string | null;
} => {
    const sundays = getSundaysForCurrentAndNextYear();
    // Copy, don't mutate — pushing Sundays onto the caller's array would leak
    // programmatic entries back into the caller's state.
    const allClosedDates = [...closedDates, ...sundays];

    // Convert strings to Date objects and sort in ascending order.
    // Local noon matches the walkforward convention and keeps the calendar
    // day stable however the entry is later read.
    const sortedDates = allClosedDates
        .map((date) => parseUkDateString(date, 12))
        .filter((date) => !isNaN(date.getTime())) // Filter out invalid dates
        .sort((a, b) => a.getTime() - b.getTime()); // Sort dates in ascending order

    // Find the index of the target date — frame-field comparison, never
    // toISOString (UTC day differs from the frame day at non-UK offsets)
    const targetISO = formatUkDateISO(parseUkDateString(targetDate));
    const targetIndex = sortedDates.findIndex(
        (date) => formatUkDateISO(date) === targetISO
    );

    // Check if target date is in the array
    if (targetIndex === -1) {
        return {
            isInArray: false,
            consecutiveDays: 0,
            nextOpenDay: null,
        };
    }

    // Count consecutive days after the target date.
    // Calendar-day difference, not an instant diff — 23h/25h DST days would
    // make the instant diff a non-integer and silently break the chain.
    let consecutiveDays = 0; // Start with 1 since we include the target date itself
    for (let i = targetIndex + 1; i < sortedDates.length; i++) {
        const currentDate = sortedDates[i];
        const previousDate = sortedDates[i - 1];

        if (daysBetween(previousDate, currentDate) === 1) {
            consecutiveDays++;
        } else {
            break;
        }
    }

    const dateChosen = parseUkDateString(targetDate, 12);
    dateChosen.setDate(dateChosen.getDate() + consecutiveDays + 1);
    const nextOpenDay = dateChosen.toLocaleDateString(LOCALE_DATE_FORMAT);

    return {
        isInArray: true,
        consecutiveDays,
        nextOpenDay,
    };
};

export default closedDatesCalculator;
